import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';
import apiService from '../services/shared/apiService';
import { Patient } from '../shared/types/src';
import PageContainer from '../components/common/PageContainer';
import ClinicalToolsNav from '../components/therapist/ClinicalToolsNav';
import MessageScheduler from '../components/therapist/MessageScheduler';
import SymbolicTrendsPanel from '../components/therapist/SymbolicTrendsPanel';
import CohortAnalysis from '../components/therapist/CohortAnalysis';

const PatientRiskDashboard = () => {
  const { themeClasses } = useTheme();
  const [selectedPatient, setSelectedPatient] = useState<string>('');
  const [riskData, setRiskData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Mock risk data
  const mockRiskData = {
    overallRisk: 'Medium',
    riskFactors: [
      { factor: 'Recent mood decline', severity: 'High', trend: 'increasing' },
      { factor: 'Missed sessions', severity: 'Medium', trend: 'stable' },
      { factor: 'Sleep disruption', severity: 'Low', trend: 'decreasing' },
      { factor: 'Social isolation', severity: 'High', trend: 'increasing' }
    ],
    protectiveFactors: [
      { factor: 'Strong support system', strength: 'High' },
      { factor: 'Medication compliance', strength: 'Medium' },
      { factor: 'Therapy engagement', strength: 'High' }
    ],
    recommendations: [
      'Schedule immediate check-in session',
      'Review safety plan with patient',
      'Consider medication adjustment',
      'Increase session frequency'
    ]
  };

  const handlePatientChange = (patientId: string) => {
    setSelectedPatient(patientId);
    if (patientId) {
      setIsLoading(true);
      // Simulate API call
      setTimeout(() => {
        setRiskData(mockRiskData);
        setIsLoading(false);
      }, 1000);
    } else {
      setRiskData(null);
    }
  };

  const getRiskColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing': return '📈';
      case 'decreasing': return '📉';
      case 'stable': return '➡️';
      default: return '➡️';
    }
  };

  return (
    <div className="space-y-6">
      <div className="p-4 border rounded-lg">
        <h3 className={`text-lg font-semibold mb-4 ${themeClasses.primaryText}`}>Select Patient for Risk Assessment</h3>
        <select
          value={selectedPatient}
          onChange={(e) => handlePatientChange(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
        >
          <option value="">Choose a patient...</option>
          <option value="patient1">Sarah Johnson</option>
          <option value="patient2">Mike Chen</option>
          <option value="patient3">Emma Davis</option>
          <option value="patient4">Alex Rodriguez</option>
        </select>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          <span className="ml-2 text-gray-600">Analyzing risk factors...</span>
        </div>
      )}

      {riskData && (
        <div className="space-y-6">
          {/* Overall Risk Level */}
          <div className="p-6 rounded-lg border-2 border-yellow-300 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div>
                <h4 className={`text-xl font-semibold ${themeClasses.primaryText}`}>Overall Risk Level</h4>
                <p className="text-gray-600">Current assessment for selected patient</p>
              </div>
              <div className={`px-4 py-2 rounded-full text-lg font-bold ${getRiskColor(riskData.overallRisk)}`}>
                {riskData.overallRisk} Risk
              </div>
            </div>
          </div>

          {/* Risk Factors */}
          <div className="p-6 rounded-lg bg-white border">
            <h4 className={`text-lg font-semibold mb-4 ${themeClasses.primaryText}`}>Risk Factors</h4>
            <div className="space-y-3">
              {riskData.riskFactors.map((factor: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{getTrendIcon(factor.trend)}</span>
                    <span className="font-medium">{factor.factor}</span>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(factor.severity)}`}>
                    {factor.severity}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Protective Factors */}
          <div className="p-6 rounded-lg bg-white border">
            <h4 className={`text-lg font-semibold mb-4 ${themeClasses.primaryText}`}>Protective Factors</h4>
            <div className="space-y-3">
              {riskData.protectiveFactors.map((factor: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="font-medium">{factor.factor}</span>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(factor.strength)}`}>
                    {factor.strength}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="p-6 rounded-lg bg-blue-50 border border-blue-200">
            <h4 className={`text-lg font-semibold mb-4 ${themeClasses.primaryText}`}>Recommended Actions</h4>
            <ul className="space-y-2">
              {riskData.recommendations.map((rec: string, index: number) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-gray-700">{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const ReportsTab = () => {
  const { user } = useAuth();
  const { themeClasses } = useTheme();
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [startDate, setStartDate] = useState<Date>(new Date(new Date().setDate(new Date().getDate() - 30)));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const fetchPatients = async () => {
      if (user?.uid) {
        try {
          const response = await apiService.getTherapistPatients(user.uid);
          setPatients(response.patients);
        } catch (error) {
          console.error("Failed to fetch patients:", error);
        }
      }
    };
    fetchPatients();
  }, [user]);

  const handlePatientSelection = (patientId: string) => {
    setSelectedPatients(prev =>
      prev.includes(patientId)
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const handleGenerateReport = async () => {
    if (selectedPatients.length !== 1 || !user?.uid) {
      alert("Please select a single patient to generate a preview.");
      return;
    }
    setIsGenerating(true);
    try {
      // Mock report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert(`Report generated for patient ${selectedPatients[0]}`);
    } catch (error) {
      console.error("Failed to generate report:", error);
      alert("Failed to generate report.");
    }
    setIsGenerating(false);
  };

  return (
    <div className="space-y-6">
      <div className="p-4 border rounded-lg space-y-4">
        <h3 className={`font-semibold text-lg ${themeClasses.primaryText}`}>Select Patients</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-48 overflow-y-auto p-2 bg-gray-50 rounded">
          {patients.map(p => (
            <div key={p.uid} className="flex items-center">
              <input
                id={`patient-${p.uid}`}
                type="checkbox"
                checked={selectedPatients.includes(p.uid)}
                onChange={() => handlePatientSelection(p.uid)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor={`patient-${p.uid}`} className={`ml-3 block text-sm font-medium ${themeClasses.primaryText}`}>
                {p.displayName}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 border rounded-lg space-y-4">
        <h3 className={`font-semibold text-lg ${themeClasses.primaryText}`}>Select Date Range</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <label className={`block text-sm font-medium ${themeClasses.primaryText}`}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate.toISOString().split('T')[0]}
              onChange={(e) => setStartDate(new Date(e.target.value))}
              className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white text-gray-900`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium ${themeClasses.primaryText}`}>
              End Date
            </label>
            <input
              type="date"
              value={endDate.toISOString().split('T')[0]}
              onChange={(e) => setEndDate(new Date(e.target.value))}
              className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white text-gray-900`}
            />
          </div>
        </div>
      </div>

      <div className="p-4 border rounded-lg space-y-4">
        <h3 className={`font-semibold text-lg ${themeClasses.primaryText}`}>Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={handleGenerateReport}
            disabled={isGenerating || selectedPatients.length !== 1}
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
          >
            {isGenerating ? 'Generating...' : 'Generate Report'}
          </button>
          <button
            onClick={() => alert('Export feature coming soon')}
            disabled={isGenerating || selectedPatients.length === 0}
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400"
          >
            Export Reports
          </button>
        </div>
      </div>
    </div>
  );
};

type View = 'trends' | 'risk' | 'reports' | 'cohort' | 'scheduler';

const ClinicalTools: React.FC = () => {
    const { user } = useAuth();
    const [currentView, setCurrentView] = useState<View>('trends');
    const [patients, setPatients] = useState<Patient[]>([]);
    const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchPatients = async () => {
            if (user?.uid) {
                try {
                    setIsLoading(true);
                    const response = await apiService.getTherapistPatients(user.uid);
                    setPatients(response.patients);
                    if (response.patients.length > 0) {
                        setSelectedPatientId(response.patients[0].uid);
                    }
                } catch (error) {
                    console.error('Failed to fetch patients:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchPatients();
    }, [user]);

    const renderView = () => {
        if (isLoading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
            );
        }

        switch (currentView) {
            case 'trends':
                return selectedPatientId ? (
                    <SymbolicTrendsPanel patientId={selectedPatientId} />
                ) : (
                    <div className="p-6 text-center text-gray-500">
                        No patients available for symbolic trends analysis.
                    </div>
                );
            case 'risk':
                return <PatientRiskDashboard />;
            case 'reports':
                return <ReportsTab />;
            case 'cohort':
                return <CohortAnalysis patients={patients} />;
            case 'scheduler':
                return <MessageScheduler />;
            default:
                return selectedPatientId ? (
                    <SymbolicTrendsPanel patientId={selectedPatientId} />
                ) : (
                    <div className="p-6 text-center text-gray-500">
                        No patients available for symbolic trends analysis.
                    </div>
                );
        }
    };

    return (
        <PageContainer>
            <div className="flex flex-col lg:flex-row gap-8">
                <aside className="lg:w-1/4">
                    <ClinicalToolsNav currentView={currentView} setCurrentView={setCurrentView} />
                </aside>
                <main className="flex-1">
                    {renderView()}
                </main>
            </div>
        </PageContainer>
    );
};

export default ClinicalTools; 